import Input from "../../components/common/input.jsx";
import Button from "../../components/common/button.jsx";
import { useFormik } from "formik";
import Joi from "joi";
import { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { publicLang, publicPath } from '../public/publicPaths';
import useAuth from "../../../context/auth.context.jsx";
import { toast } from "react-hot-toast";
import logo from "../../assets/logo.png";
import GoogleAuthFlow from "../../components/googleAuth.jsx";
import AppleAuthFlow from "../../components/appleAuth.jsx";

function LogIn() {
  const { user, logIn } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(["auth", "common"]);

  const { getFieldProps, handleSubmit, touched, errors, isSubmitting } = useFormik({
    initialValues: { email: "", password: "" },
    validate(values) {
      const schema = Joi.object({
        email: Joi.string().required().email({ tlds: false }).messages({
          "string.email": t("auth:emailInvalid"),
          "string.empty": t("auth:emailRequired"),
        }),
        password: Joi.string().required().messages({
          "string.empty": t("auth:passwordRequired"),
        }),
      });
      const { error } = schema.validate(values, { abortEarly: false });
      if (!error) return null;
      const errs = {};
      for (let item of error.details) errs[item.path[0]] = item.message;
      return errs;
    },
    onSubmit: async (values) => {
      try {
        await logIn(values);
        navigate("/");
      } catch (error) {
        toast.error(error.response?.data?.error || t("auth:loginFailed"));
      }
    },
  });

  // Only for someone who was already signed in when they opened this screen. A
  // sign-in made here navigates on its own (new accounts go to /welcome), and this
  // effect firing after it sent them to / instead.
  const hadUser = useRef(!!user);
  useEffect(() => {
    if (user && hadUser.current) navigate("/");
  }, [user, navigate]);

  return (
    <div className="auth-page">
      <div className="text-center mb-4">
        <img src={logo} alt="Master Splitter" width="72" height="72" style={{ borderRadius: 18 }} />
        <h1 className="fs-3 fw-bold mt-3 mb-1">Master Splitter</h1>
        <p className="text-muted-ms">{t("auth:tagline")}</p>
      </div>

      {/* App Review 5.1.1(v): the games must be reachable without signing up, so
          this sits on the first screen, not behind the registration flow. */}
      <div className="text-center mb-3">
        <button type="button" className="btn-ms btn-ms-link" onClick={() => navigate("/guest/games")}>
          {t("auth:guestContinue")}
        </button>
        <p className="text-muted-ms mb-0" style={{ fontSize: "0.8rem" }}>{t("auth:guestNote")}</p>
      </div>
      <form onSubmit={handleSubmit} className="ms-card">
        <Input
          {...getFieldProps("email")}
          name="email"
          label={t("auth:email")}
          type="email"
          autoComplete="email"
          error={touched.email ? errors.email : ""}
          placeholder="name@domain.com"
          required
        />
        <Input
          {...getFieldProps("password")}
          name="password"
          label={t("auth:password")}
          type="password"
          autoComplete="current-password"
          error={touched.password ? errors.password : ""}
          required
        />
        <Button type="submit" text={t("auth:login")} disabled={isSubmitting} />
        <button
          type="button"
          className="btn-ms btn-ms-link btn-ms-block mt-2"
          onClick={() => navigate("/forgot-password")}
        >
          {t("auth:forgotPassword")}
        </button>
        <button
          type="button"
          className="btn-ms btn-ms-outline btn-ms-block mt-2"
          onClick={() => navigate("/register")}
        >
          {t("auth:noAccountRegister")}
        </button>
        <GoogleAuthFlow />
        <AppleAuthFlow />
      </form>
      <nav className="auth-legal-links" aria-label={t("common:legalInfo")}>
        <Link to={publicPath(publicLang(i18n.language), '/about')}>{t("common:about")}</Link>
        <Link to={publicPath(publicLang(i18n.language), '/privacy')}>{t("common:privacy")}</Link>
        <Link to={publicPath(publicLang(i18n.language), '/terms')}>{t("common:terms")}</Link>
      </nav>
    </div>
  );
}

export default LogIn;
